// pages/MediaScreenDetail/DeliverySection.js
//
// Delivery & media value — one temporal instrument, not a pile of charts.
//
//   Overview   — four equal groups: Media value / Rates / Delivery quality /
//                Audience. Assumptions live in notes, never inline labels.
//   Time panel — THE fix for the earlier chaos: media value (₹/h line lane)
//                and targeting mix (100% bar lane) share ONE time axis and
//                ONE crosshair; a single tooltip reads every series at that
//                hour. Two phenomena, one clock.
//   Daily bars — (7d) witnessed% / occupied% per day, hover per point.
//   Creative   — NAMED creatives and campaigns (super-admin scope), value by
//                format alongside.
//
// Semantics per billing-service source; palette validated (six checks PASS).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { ggosDeviceService } from '../../services/api';
import './DeliverySection.css';

const WINDOWS = [{ h: 24, label: '24h' }, { h: 168, label: '7d' }];
const COLORS = { rtb_win: '#4f46e5', presence: '#0d9488', fallback: '#b45309', idle: '#d3d7de' };
const LABELS = { rtb_win: 'RTB win', presence: 'viewer-targeted', fallback: 'rotation', idle: 'idle' };
const VALUE = '#4f46e5', OCC = '#0d9488';

const fmtHour = (iso) => new Date(iso).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false, hour: '2-digit' }) + 'h';
const fmtDay = (iso) => new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short' });
// IST calendar-day ms (server sends 'YYYY-MM-DD') — format in UTC so the day never shifts.
const fmtDayMs = (ms) => new Date(ms).toLocaleDateString('en-IN', { timeZone: 'UTC', day: '2-digit', month: 'short' });

// ── The time panel: value lane + mix lane, one axis, one crosshair. ─────────
function TimePanel({ mix, valueHourly, hours }) {
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);
  useEffect(() => { setHover(null); }, [mix, valueHourly]);
  // Dense hourly grid across the FULL window. Indexing only the non-empty
  // server buckets would compress dark time out of the axis (46 active hours
  // would read as a continuous week) — absent hours must render as gaps.
  const mixBy = new Map(mix.map(m => [new Date(m.t).toISOString(), m]));
  const valBy = new Map(valueHourly.map(v => [new Date(v.t).toISOString(), v.inr]));
  const HOUR = 3600000;
  const lastBucket = Math.floor(Date.now() / HOUR) * HOUR;
  const grid = Array.from({ length: hours + 1 }, (_, i) => new Date(lastBucket - (hours - i) * HOUR).toISOString());
  if (!mix.length && !valueHourly.length) return <div className="del-empty">No delivery telemetry in this window.</div>;

  const W = 960, ML = 44, MR = 10;
  const LANE1_T = 26, LANE1_H = 96;                 // value lane (label rides above the lane)
  const LANE2_T = LANE1_T + LANE1_H + 30, LANE2_H = 84; // mix lane
  const H = LANE2_T + LANE2_H + 24;
  const iw = W - ML - MR;
  const bw = iw / grid.length;
  const barW = Math.min(Math.max(bw - 2, 1), 24);   // ≤24px thick — band air, never fill the slot
  const X = (i) => ML + (i + 0.5) * bw;             // band centre — line, bars and crosshair agree
  const vmax = Math.max(1, ...valueHourly.map(v => v.inr));
  const Yv = (v) => LANE1_T + (1 - v / vmax) * LANE1_H;
  const KEYS = ['rtb_win', 'presence', 'fallback', 'idle'];

  const valuePath = (() => {
    let d = '', pen = false;
    grid.forEach((t, i) => {
      const v = valBy.get(t);
      if (v == null) { pen = false; return; }
      d += `${pen ? 'L' : 'M'}${X(i).toFixed(1)},${Yv(v).toFixed(1)}`; pen = true;
    });
    return d;
  })();

  const nearest = (clientX) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const fx = ((clientX - rect.left) / rect.width) * W;
    return Math.max(0, Math.min(grid.length - 1, Math.floor((fx - ML) / bw)));
  };
  const ht = hover != null ? grid[hover] : null;
  const hm = ht ? mixBy.get(ht) : null;
  const hv = ht ? valBy.get(ht) : null;
  const hmTotal = hm ? KEYS.reduce((t, k) => t + hm[k], 0) + (hm.other || 0) : 0;

  return (
    <div className="del-chart-wrap" ref={wrapRef}>
      <svg viewBox={`0 0 ${W} ${H}`} className="del-svg" role="img" tabIndex={0}
        aria-label="Media value and targeting mix per hour; arrow keys move the readout"
        onMouseMove={e => setHover(nearest(e.clientX))}
        onMouseLeave={() => setHover(null)}
        onKeyDown={e => { if (e.key === 'ArrowLeft') { setHover(h => Math.max(0, (h ?? grid.length - 1) - 1)); e.preventDefault(); } if (e.key === 'ArrowRight') { setHover(h => Math.min(grid.length - 1, (h ?? 0) + 1)); e.preventDefault(); } }}>
        {/* value lane */}
        <text x={ML} y={14} className="del-lane-label">MEDIA VALUE ₹/H</text>
        {[0, vmax].map(v => (
          <g key={v}>
            <line x1={ML} y1={Yv(v)} x2={W - MR} y2={Yv(v)} stroke="#eceef3" strokeWidth="1" />
            <text x={ML - 6} y={Yv(v) + 3} textAnchor="end" className="del-tick">₹{Math.round(v)}</text>
          </g>
        ))}
        <path d={valuePath} fill="none" stroke={VALUE} strokeWidth="2" strokeLinejoin="round" />
        {/* mix lane */}
        <text x={ML} y={LANE2_T - 10} className="del-lane-label">TARGETING MIX</text>
        {grid.map((t, i) => {
          const m = mixBy.get(t);
          if (!m) return null;
          const total = KEYS.reduce((s, k) => s + m[k], 0) + (m.other || 0);
          if (!total) return null;
          let y = LANE2_T + LANE2_H;
          return (
            <g key={t}>
              {KEYS.map(k => {
                const h = (m[k] / total) * LANE2_H;
                y -= h;
                return h > 0 ? <rect key={k} x={X(i) - barW / 2} y={y} width={barW} height={Math.max(h - (h > 3 ? 2 : 0), 0.5)} rx={h > 6 ? 2 : 0} fill={COLORS[k]} opacity={hover === i ? 1 : 0.9} /> : null;
              })}
            </g>
          );
        })}
        {/* shared x axis — hour ticks for a day, IST day boundaries for a week */}
        {grid.map((t, i) => {
          if (hours <= 24) return i % 3 === 0 ? <text key={t} x={X(i)} y={H - 6} textAnchor="middle" className="del-tick">{fmtHour(t)}</text> : null;
          return i > 0 && fmtDay(t) !== fmtDay(grid[i - 1])
            ? <text key={t} x={X(i)} y={H - 6} textAnchor="middle" className="del-tick">{fmtDay(t)}</text> : null;
        })}
        {/* one crosshair across both lanes */}
        {ht && (
          <g>
            <line x1={X(hover)} y1={LANE1_T} x2={X(hover)} y2={LANE2_T + LANE2_H} stroke="#c3c8d4" strokeWidth="1" />
            {hv != null && <><circle cx={X(hover)} cy={Yv(hv)} r="6" fill="#fff" /><circle cx={X(hover)} cy={Yv(hv)} r="4" fill={VALUE} /></>}
          </g>
        )}
      </svg>
      {ht && (
        <div className="del-tip" style={{ left: `${Math.min(92, Math.max(8, (X(hover) / W) * 100))}%` }}>
          <div className="del-tip-val">₹{(+(hv ?? 0)).toFixed(2)}</div>
          <div className="del-tip-row">{fmtDay(ht)} {fmtHour(ht)}</div>
          {hm ? KEYS.map(k => {
            if (!(hm[k] > 0)) return null;
            const pct = Math.round(100 * hm[k] / hmTotal);
            return <div key={k} className="del-tip-row"><i style={{ background: COLORS[k] }} /> {LABELS[k]}: {pct === 0 ? '<1' : pct}%</div>;
          }) : <div className="del-tip-row">no play telemetry</div>}
        </div>
      )}
    </div>
  );
}

// ── Daily verification/audience (7d): days on a TRUE time axis — a dark day
//    is a visible gap, never a straight line drawn across it. ────────────────
function DailyLines({ daily }) {
  const [hover, setHover] = useState(null);
  useEffect(() => { setHover(null); }, [daily]);
  const ok = daily && daily.length >= 2 && daily[0].day;
  const DAY = 86400000;
  const first = ok ? Date.parse(daily[0].day) : 0;
  const span = ok ? Math.max(1, Math.round((Date.parse(daily[daily.length - 1].day) - first) / DAY)) : 1;
  if (!ok) return null;
  const slot = new Map(daily.map(d => [Math.round((Date.parse(d.day) - first) / DAY), d]));
  const W = 960, H = 130, ML = 34, MR = 46, MT = 10, MB = 20;
  const iw = W - ML - MR, ih = H - MT - MB;
  const X = (i) => ML + (i / span) * iw;
  const Y = (v) => MT + (1 - v / 100) * ih;
  const path = (get) => {
    let d = '', pen = false;
    for (let i = 0; i <= span; i++) {
      const r = slot.get(i);
      if (!r) { pen = false; continue; }
      d += `${pen ? 'L' : 'M'}${X(i).toFixed(1)},${Y(get(r)).toFixed(1)}`; pen = true;
    }
    return d;
  };
  const WIT = VALUE;
  const hd = hover != null ? slot.get(hover) : null;
  const move = (clientX, rect) => setHover(Math.max(0, Math.min(span, Math.round(((clientX - rect.left) / rect.width * W - ML) / (iw / span)))));
  return (
    <div className="del-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="del-svg" role="img" tabIndex={0}
        aria-label="Daily witnessed and occupied shares of plays; arrow keys move the readout"
        onMouseMove={e => move(e.clientX, e.currentTarget.getBoundingClientRect())}
        onMouseLeave={() => setHover(null)}
        onKeyDown={e => { if (e.key === 'ArrowLeft') { setHover(h => Math.max(0, (h ?? span) - 1)); e.preventDefault(); } if (e.key === 'ArrowRight') { setHover(h => Math.min(span, (h ?? 0) + 1)); e.preventDefault(); } }}>
        {[0, 50, 100].map(v => (
          <g key={v}>
            <line x1={ML} y1={Y(v)} x2={W - MR} y2={Y(v)} stroke="#eceef3" strokeWidth="1" />
            <text x={ML - 6} y={Y(v) + 3} textAnchor="end" className="del-tick">{v}</text>
          </g>
        ))}
        {Array.from({ length: span + 1 }, (_, i) => (
          <text key={i} x={X(i)} y={H - 5} textAnchor="middle" className="del-tick">{fmtDayMs(first + i * DAY)}</text>
        ))}
        <path d={path(d => d.witnessedPct)} fill="none" stroke={WIT} strokeWidth="2" strokeLinejoin="round" />
        <path d={path(d => d.occupiedPct)} fill="none" stroke={OCC} strokeWidth="2" strokeLinejoin="round" />
        {[...slot.entries()].map(([i, d]) => (
          <g key={i}>
            <circle cx={X(i)} cy={Y(d.witnessedPct)} r="5.5" fill="#fff" /><circle cx={X(i)} cy={Y(d.witnessedPct)} r="3.5" fill={WIT} />
            <circle cx={X(i)} cy={Y(d.occupiedPct)} r="5.5" fill="#fff" /><circle cx={X(i)} cy={Y(d.occupiedPct)} r="3.5" fill={OCC} />
          </g>
        ))}
        {hover != null && <line x1={X(hover)} y1={MT} x2={X(hover)} y2={MT + ih} stroke="#c3c8d4" strokeWidth="1" />}
        {(() => {
          const d = daily[daily.length - 1];
          let yW = Y(d.witnessedPct) + 3, yO = Y(d.occupiedPct) + 3;
          if (Math.abs(yW - yO) < 12) { const mid = (yW + yO) / 2, s = yW <= yO ? 1 : -1; yW = mid - s * 6; yO = mid + s * 6; }
          return (<>
            <text x={X(span) + 8} y={yW} className="del-endlabel">{d.witnessedPct}</text>
            <text x={X(span) + 8} y={yO} className="del-endlabel">{d.occupiedPct}</text>
          </>);
        })()}
      </svg>
      {hover != null && (
        <div className="del-tip" style={{ left: `${Math.min(92, Math.max(8, (X(hover) / W) * 100))}%` }}>
          <div className="del-tip-val">{hd ? hd.label : fmtDayMs(first + hover * DAY)}</div>
          {hd ? (<>
            <div className="del-tip-row"><i style={{ background: WIT }} /> witnessed {hd.witnessedPct}%</div>
            <div className="del-tip-row"><i style={{ background: OCC }} /> occupied {hd.occupiedPct}%</div>
            <div className="del-tip-row">plays: {hd.plays.toLocaleString()}</div>
          </>) : <div className="del-tip-row">no plays this day</div>}
        </div>
      )}
    </div>
  );
}

export default function DeliverySection({ thingName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(24);
  const seqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++seqRef.current;
    try {
      setLoading(true);
      const json = await ggosDeviceService.getScreenDelivery(thingName, hours);
      if (seq !== seqRef.current) return;
      if (!json.success) throw new Error(json.error || 'failed');
      setData(json.data);
      setError(null);
    } catch (e) { if (seq === seqRef.current) setError(e.message); }
    finally { if (seq === seqRef.current) setLoading(false); }
  }, [thingName, hours]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const st = data?.stats;

  return (
    <div className="del-section">
      <div className="del-head">
        <h2>Delivery &amp; media value</h2>
        <div className="del-windows">
          {WINDOWS.map(w => (
            <button key={w.h} className={hours === w.h ? 'is-active' : ''} onClick={() => setHours(w.h)}>{w.label}</button>
          ))}
        </div>
        <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'del-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && !data && <div className="del-skel" />}

      {st && (
        <div className={loading ? 'del-dim' : undefined}>
          <div className="del-overview">
            <div className="del-hero">
              <div className="del-hero-val">₹{st.mediaValueInr.toLocaleString()}</div>
              <div className="del-hero-state">media value</div>
              <div className="del-hero-obs">₹{st.settledInr.toLocaleString()} settled · ₹{st.pendingInr.toLocaleString()} pricing</div>
            </div>
            <div className="del-group">
              <div className="del-group-title">Rates</div>
              <div className="del-group-row"><span>effective CPM</span><b>{st.effectiveCpmInr != null ? `₹${st.effectiveCpmInr}` : '—'}</b></div>
              <div className="del-group-row"><span>quoted CPM</span><b>₹{st.quotedCpmInr}</b></div>
              <div className="del-group-row"><span>per play</span><b>{st.valuePerPlayInr != null ? `₹${st.valuePerPlayInr}` : '—'}</b></div>
            </div>
            <div className="del-group">
              <div className="del-group-title">Delivery quality</div>
              <div className="del-group-row"><span>plays</span><b>{st.plays.toLocaleString()}</b></div>
              <div className="del-group-row"><span>billing coverage</span><b className={st.billingCoveragePct < 90 ? 'is-bad' : ''}>{st.billingCoveragePct ?? '—'}%</b></div>
              <div className="del-group-row"><span>witnessed</span><b className={st.witnessedPct < 95 ? 'is-bad' : ''}>{st.witnessedPct ?? '—'}%</b></div>
            </div>
            <div className="del-group">
              <div className="del-group-title">Audience</div>
              <div className="del-group-row"><span>played while occupied</span><b>{st.occupiedPct ?? '—'}%</b></div>
              <div className="del-group-row"><span>viewer-targeted</span><b>{st.targetedPct ?? '—'}%</b></div>
              <div className="del-group-row"><span>high-attention events</span><b>{st.attentionEvents.toLocaleString()}</b></div>
            </div>
          </div>
          <div className="del-footnote">
            Effective CPM = quoted ₹{st.quotedCpmInr} × format multipliers (30s bills 3×). Priced at {st.pricedPassengersPerSlot} passengers/slot (assumption); sensed faces/tick during plays: {st.sensedFacesPerPlayTick ?? '—'} (visibility, not exposure — different unit).
          </div>

          <div className="del-card">
            <div className="del-card-head">
              <div className="del-card-title">The screen's {hours <= 24 ? 'day' : 'week'}</div>
              <div className="del-legend">
                <span className="del-key"><i style={{ background: VALUE, height: 2 }} /> ₹/h</span>
                {Object.keys(COLORS).map(k => (
                  <span key={k} className="del-key"><i style={{ background: COLORS[k] }} /> {LABELS[k]}</span>
                ))}
              </div>
            </div>
            <TimePanel mix={data.mix} valueHourly={data.valueHourly || []} hours={data.hours} />
          </div>

          {data.claimsDaily && (
            <div className="del-card">
              <div className="del-card-head">
                <div className="del-card-title">Daily integrity &amp; audience</div>
                <div className="del-legend">
                  <span className="del-key"><i style={{ background: VALUE, height: 2 }} /> witnessed %</span>
                  <span className="del-key"><i style={{ background: OCC, height: 2 }} /> occupied %</span>
                </div>
              </div>
              <DailyLines daily={data.claimsDaily} />
            </div>
          )}

          <div className="del-cols">
            <div className="del-card">
              <div className="del-card-head"><div className="del-card-title">Creative mix</div></div>
              <table className="del-table">
                <thead><tr><th>creative</th><th>campaign</th><th>source</th><th>plays</th><th>occupied</th></tr></thead>
                <tbody>
                  {data.creatives.map(c => (
                    <tr key={c.creative_id}>
                      <td>{c.creativeName || <span className="del-mono">{(c.creative_id || '').slice(0, 8)}</span>}</td>
                      <td className="del-dim-txt">{c.campaignName || '—'}</td>
                      <td><i className="del-chip" style={{ background: COLORS[c.reason] || '#d3d7de' }} /> {LABELS[c.reason] || c.reason}</td>
                      <td>{c.plays.toLocaleString()}</td>
                      <td>{c.occupiedPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="del-card">
              <div className="del-card-head"><div className="del-card-title">Value by format</div></div>
              <table className="del-table">
                <thead><tr><th>format</th><th>buying</th><th>plays</th><th>value</th></tr></thead>
                <tbody>
                  {(data.valueByType || []).map((v, i) => (
                    <tr key={i}>
                      <td>{v.format === '(base)' ? 'base 10s' : v.format.replace(/_/g, ' ').replace(' image or video', 's')}</td>
                      <td>{v.buying_type}</td>
                      <td>{v.plays.toLocaleString()}</td>
                      <td><b>₹{v.inr.toLocaleString()}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
